import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TestimonialsSlider from "../components/TestimonialsSlider";
import FAQSection from "../components/FAQSection";
import ContactModal from "../components/ContactModal";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  Target,
  Shield,
  Award,
  Users,
  Clock,
  TrendingUp,
  CheckCircle,
  Heart,
  Zap,
} from "lucide-react";

const AboutUsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Navbar onOpenModal={() => setIsModalOpen(true)} />

      {/* Hero Section - About the Company */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
                About{" "}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Trademark Law Associates
                </span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Your Trusted Partner in Intellectual Property Protection and
                Business Growth
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mt-16">
              <div className="animate-fade-in">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800"
                  alt="Our Team"
                  className="rounded-2xl shadow-2xl hover-zoom"
                />
              </div>
              <div className="space-y-6 animate-slide-up">
                <h2 className="text-4xl font-bold text-gray-800">Who We Are</h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Trademark Law Associates is a leading provider of intellectual
                  property and business formation services in the USA. Founded
                  with a mission to make trademark registration and business
                  services accessible to everyone, we've helped thousands of
                  businesses protect their brands and achieve their goals.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  With years of experience and a team of dedicated
                  professionals, we specialize in trademark registration,
                  copyright protection, business formation, and comprehensive
                  brand services. Our commitment to excellence and customer
                  satisfaction sets us apart in the industry.
                </p>
                <div className="flex flex-wrap gap-4 pt-4">
                  <div className="flex items-center space-x-2">
                    <Award className="text-brand-primary" size={24} />
                    <span className="font-semibold text-gray-700">
                      10,000+ Clients Served
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Shield className="text-brand-primary" size={24} />
                    <span className="font-semibold text-gray-700">
                      98% Success Rate
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="text-brand-primary" size={24} />
                    <span className="font-semibold text-gray-700">
                      24/7 Support
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision and Mission */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                Our Vision &{" "}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Mission
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Guiding principles that drive everything we do
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Vision */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all animate-scale-in">
                <div className="bg-brand-gradient w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Target className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Our Vision
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  To be the most trusted and innovative provider of intellectual
                  property services, empowering businesses of all sizes to
                  protect their brands and achieve lasting success in the
                  competitive marketplace.
                </p>
              </div>

              {/* Mission */}
              <div
                className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all animate-scale-in"
                style={{ animationDelay: "0.1s" }}
              >
                <div className="bg-brand-gradient w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Heart className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Our Mission
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  To provide accessible, affordable, and professional trademark
                  registration and business services that enable entrepreneurs
                  and businesses to secure their intellectual property with
                  confidence and ease.
                </p>
              </div>
            </div>

            {/* Core Values */}
            <div className="mt-16 grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Shield,
                  title: "Integrity",
                  desc: "We operate with transparency and honesty in every interaction",
                },
                {
                  icon: Zap,
                  title: "Excellence",
                  desc: "We deliver exceptional quality and results in all our services",
                },
                {
                  icon: Users,
                  title: "Customer-First",
                  desc: "Your success and satisfaction are our top priorities",
                },
              ].map((value, idx) => {
                const Icon = value.icon;
                return (
                  <div
                    key={idx}
                    className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all animate-scale-in"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <Icon
                      className="text-brand-primary mx-auto mb-4"
                      size={40}
                    />
                    <h4 className="text-xl font-bold text-gray-800 mb-2">
                      {value.title}
                    </h4>
                    <p className="text-gray-600">{value.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                Why Choose{" "}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Trademark Law Associates
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We go above and beyond to ensure your success
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Target,
                  title: "Expert Team",
                  desc: "Our experienced professionals have deep knowledge in trademark law and business services",
                },
                {
                  icon: Clock,
                  title: "Fast Processing",
                  desc: "We process applications within 24 hours and handle all the paperwork efficiently",
                },
                {
                  icon: Award,
                  title: "98% Success Rate",
                  desc: "Our meticulous approach ensures high approval rates for trademark applications",
                },
                {
                  icon: Users,
                  title: "24/7 Support",
                  desc: "Round-the-clock customer service to answer all your questions and concerns",
                },
                {
                  icon: Shield,
                  title: "Comprehensive Protection",
                  desc: "Full-service solutions from trademark search to registration and monitoring",
                },
                {
                  icon: TrendingUp,
                  title: "Affordable Pricing",
                  desc: "Transparent, competitive pricing with no hidden fees - starting at just $45",
                },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 animate-scale-in"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <div className="bg-brand-gradient w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Button
                size="lg"
                className="bg-brand-gradient text-white hover:opacity-90 group shadow-lg px-8 py-6 text-lg font-bold"
                onClick={() => setIsModalOpen(true)}
              >
                Get Started Today
                <ArrowRight
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                  size={24}
                />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <TestimonialsSlider /> */}

      {/* FAQs */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-xl text-emerald-50 mb-8 leading-relaxed">
              Join thousands of satisfied clients who trust us with their
              intellectual property. Let's secure your business future together.
            </p>
            <Button
              size="lg"
              onClick={() => setIsModalOpen(true)}
              className="bg-white text-brand-primary hover:bg-gray-50 shadow-xl px-10 py-6 text-lg font-bold border-2 border-white"
            >
              Contact Us Now
              <ArrowRight className="ml-2" size={24} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default AboutUsPage;
